import {Button, Checkbox, Input, Modal, Select} from 'antd';
import type {CheckboxChangeEvent} from 'antd/es/checkbox';
import {ReactNode, UIEvent, useCallback, useEffect, useMemo, useState} from 'react';

import {appRoute} from '../../../../app-route';
import {Text} from '../../../../component/text/text';
import {Empty} from '../../../../layout/empty/empty';
import {NavigationLink} from '../../../../layout/navigation-link/navigation-link';
import {Spinner} from '../../../../layout/spinner/spinner';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {AnalyticsTarget, track} from '../../../../service/analytics/analytics';
import {useDebouncedValue} from '../../../../service/debounce-hook/debounce-hook';
import {TagDataType, TagGroupType, useInfiniteTags, useTagGroupList} from '../../../../service/reviews/reviews-tags';
import {ReviewDataTagType} from '../../../../service/reviews/reviews-type';
import {classNames} from '../../../../util/css';
import {onScrollInfiniteScrollContainer} from '../../../../util/infinite-scroll';

import {searchIcon} from './tags-popup-helper';
import * as styles from './tags-popup.scss';

const {Option} = Select;

type TagsPopupVariantType = 'default' | 'bulk';

function buildInitialTagSelection(
    variant: TagsPopupVariantType,
    currentTags: Array<ReviewDataTagType>
): Array<TagDataType> {
    if (variant === 'bulk') {
        return [];
    }

    return currentTags
        .filter((tag) => !tag.isAspect)
        .map((tag) => ({
            id: tag.id,
            title: tag.title,
            keys: [],
            keywords: [],
            folder: tag.folderId ? {id: tag.folderId, title: '', isRequired: false} : null,
            user: {id: 0},
        }));
}

type PropsType = {
    className?: string;
    currentTags: Array<ReviewDataTagType>;
    isCreating: boolean;
    isOpen: boolean;
    onClose: () => void;
    onApply: (tagList: Array<TagDataType>) => void | Promise<void>;
    variant?: TagsPopupVariantType;
    title?: ReactNode;
};

export function TagsPopup(props: PropsType): JSX.Element {
    const {className, onClose, isOpen, isCreating, onApply, currentTags, variant = 'default', title} = props;
    const [searchFilter, setSearchFilter] = useState<string>('');
    const [selectedTagGroupId, setSelectedTagGroupId] = useState<number>(0);
    const [selectedItemList, setSelectedItemList] = useState<Array<TagDataType>>([]);

    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    const {getLocalizedString} = useLocale();

    const {data: tagGroupListResult, isFetching: tagGroupListIsInProgress} = useTagGroupList(isOpen);
    const debouncedSearch = useDebouncedValue(searchFilter, '');

    const {
        data,
        refetch: reload,
        isFetching: allTagsListIsInProgress,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteTags(
        {q: debouncedSearch, withoutRg: true, folderIds: selectedTagGroupId, withoutFolder: null},
        isOpen
    );

    useEffect(() => {
        if (isOpen && !isInitialized) {
            setSelectedItemList(buildInitialTagSelection(variant, currentTags));
            setIsInitialized(true);
        }

        if (!isOpen && isInitialized) {
            setIsInitialized(false);
        }
    }, [isOpen, currentTags, isInitialized, variant]);

    const handleClose = useCallback(() => {
        setSelectedItemList([]);
        setIsInitialized(false);
        onClose();
    }, [onClose]);

    const tagGroupList: Array<TagGroupType> = useMemo(() => tagGroupListResult || [], [tagGroupListResult]);
    const tagList = data?.pages.flatMap((element) => element.results) || [];

    const toggleTagSelection = useCallback((item: TagDataType, nextChecked: boolean) => {
        setSelectedItemList((previous) => {
            const isSelected = previous.some((selectedItem) => selectedItem.id === item.id);

            if (nextChecked && !isSelected) {
                return [...previous, item];
            }

            if (!nextChecked && isSelected) {
                return previous.filter((selectedItem) => selectedItem.id !== item.id);
            }

            return previous;
        });
    }, []);

    const renderListItem = useCallback(
        (allCompanyItem: TagDataType): JSX.Element => {
            const {id, title: tagTitle, folder} = allCompanyItem;

            const isSelected = selectedItemList.some((selectedItem: TagDataType): boolean => selectedItem.id === id);

            function handleCheckboxChange(event: CheckboxChangeEvent) {
                toggleTagSelection(allCompanyItem, event.target.checked);
            }

            const rowClassName = classNames(styles.TagsPopup_listItemRow, {
                [styles.TagsPopup_listItemRow_selected]: isSelected,
            });

            return (
                <li className={styles.TagsPopup_listItem} key={id}>
                    <div className={rowClassName}>
                        <Checkbox checked={isSelected} onChange={handleCheckboxChange}>
                            <div className={styles.TagsPopup_listItemContent}>
                                <div className={styles.TagsPopup_listItemTitle}>
                                    {tagTitle}{' '}
                                    <Text secondary small>
                                        {selectedTagGroupId === 0 && folder?.title}
                                    </Text>
                                </div>
                            </div>
                        </Checkbox>
                    </div>
                </li>
            );
        },
        [selectedItemList, toggleTagSelection, selectedTagGroupId]
    );

    async function handleSubmit() {
        track(
            AnalyticsTarget.ReviewsManagement.Reviews.AddTag,
            selectedItemList.map((item: TagDataType) => item.id)
        );

        try {
            await Promise.resolve(onApply(selectedItemList));
        } catch {
            // Parent handles errors (e.g. snackbar); avoid unhandled rejection
        }
    }

    function onContainerScroll(event: UIEvent) {
        if (!allTagsListIsInProgress && hasNextPage) {
            onScrollInfiniteScrollContainer(event, () => fetchNextPage());
        }
    }

    const applyDisabled = isCreating || (variant === 'bulk' && selectedItemList.length === 0);

    const modalTitle = title ?? <Locale stringKey="REVIEWS__TAGS_POPUP__HEADER" />;

    return (
        <Modal
            className={classNames(styles.TagsPopup, className)}
            footer={
                <div className={styles.TagsPopup_footer}>
                    <Button className={styles.TagsPopup_footerButton} key="cancel" onClick={handleClose}>
                        <Locale stringKey="POPUP__BUTTON__CANCEL" />
                    </Button>
                    <Button
                        className={styles.TagsPopup_footerButton}
                        disabled={applyDisabled}
                        key="apply"
                        loading={isCreating}
                        onClick={() => {
                            handleSubmit();
                        }}
                        type="primary"
                    >
                        <Locale stringKey="TEXT__APPLY" />
                    </Button>
                </div>
            }
            onCancel={handleClose}
            open={isOpen}
            title={modalTitle}
            width={520}
        >
            <div className={styles.TagsPopup_filters}>
                <Input
                    className={styles.TagsPopup_searchInput}
                    onInput={(event) => {
                        const {value} = event.currentTarget;

                        setSearchFilter(value.trim());
                    }}
                    placeholder={getLocalizedString('REVIEWS__TAGS_POPUP__SEARCH_FOR_TAGS')}
                    suffix={searchIcon}
                />

                <Select<number>
                    className={styles.TagsPopup_filterSelect}
                    disabled={tagGroupListIsInProgress}
                    filterOption={() => true}
                    loading={tagGroupListIsInProgress}
                    onChange={setSelectedTagGroupId}
                    placeholder={getLocalizedString('REVIEWS__TAGS_POPUP__ALL_GROUPS')}
                    value={selectedTagGroupId}
                >
                    <Option key="all-groups" value={0}>
                        <Locale stringKey="REVIEWS__TAGS_POPUP__ALL_GROUPS" />
                    </Option>

                    {tagGroupList.map((tagGroupItem: TagGroupType): JSX.Element => {
                        const {id, title: groupTitle} = tagGroupItem;

                        return (
                            <Option key={id} value={id}>
                                {groupTitle}
                            </Option>
                        );
                    })}
                </Select>
            </div>

            <div className={styles.TagsPopup_listShell}>
                <div className={styles.TagsPopup_scrollArea} onScroll={onContainerScroll}>
                    {tagList.length > 0 && <ul className={styles.TagsPopup_list}>{tagList.map(renderListItem)}</ul>}

                    {!allTagsListIsInProgress && tagList.length === 0 && (
                        <Empty
                            mainText="EMPTY__THERE_IS_NOTHING_HERE_YET"
                            secondaryText="EMPTY__TRY_CHANGING_FILTERS"
                        />
                    )}
                    {allTagsListIsInProgress && (
                        <div className={styles.TagsPopup_spinner}>
                            <Spinner />
                        </div>
                    )}
                </div>

                <div className={styles.TagsPopup_buttonsWrapper}>
                    <NavigationLink
                        className={styles.TagsPopup_tagSetupLink}
                        isTargetBlank
                        to={appRoute.reviewsManagementTags.path}
                    >
                        <Locale stringKey="REVIEWS__TAGS_POPUP__GO_TO_TAG_SETUP" />
                    </NavigationLink>
                    <Button className={styles.TagsPopup_refreshButton} onClick={() => reload()} type="text">
                        <Locale stringKey="REVIEWS__TAGS_POPUP__REFRESH" />
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
